import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { formatAmount, formatDateOnly, getFormatConfig, initializeFormats } from '../../../utils/global';
import { useApHub } from '../ApHub/ApHubContext';
import ApHubBreadcrumb from '../ApHub/ApHubBreadcrumb';
import PaymentBatchDetailDialog from './PaymentBatchDetailDialog';
import {
  countBatchPayments,
  fetchPaymentBatches,
  getBankDisplayName,
  summarizeBatchBanks,
} from './paymentBatchListUtils';
import {
  harmonatePageSx,
  harmonatePageTitleSx,
  harmonateCardSx,
  harmonateDataGridSx,
  harmonateSectionHeaderSx,
  harmonatePlainIconButtonSx,
} from '../../../theme/harmonateStyles';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const PaymentBatches = () => {
  const tenantId = getTenantId();
  const config = getFormatConfig();
  const { selectedBusinessUnitId, selectedBusinessUnitName } = useApHub();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [banks, setBanks] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const resolveBankName = useCallback((bankId) => {
    const bank = banks.find((item) => item.bankId === bankId);
    return getBankDisplayName(bank);
  }, [banks]);

  const loadBatches = useCallback(async (options = {}) => {
    const { silent = false } = options;
    if (!tenantId) {
      setRows([]);
      if (!silent) setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
        setError(null);
      }

      const [batchList, bankList] = await Promise.all([
        fetchPaymentBatches(tenantId, selectedBusinessUnitId),
        apiFetch(`conductor/cash-recon/banks/by-tenant-and-status?tenant_id=${tenantId}&status=ACTIVE`).catch(() => []),
      ]);

      setBanks(Array.isArray(bankList) ? bankList : []);
      setRows(batchList);
    } catch (err) {
      if (!silent) setError(err.message || 'Failed to load payment batches');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [tenantId, selectedBusinessUnitId]);

  useEffect(() => {
    if (tenantId) {
      initializeFormats(tenantId);
    }
    loadBatches();
  }, [tenantId, loadBatches]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((row) => (
      (row.batchNumber || '').toLowerCase().includes(q)
      || (row.batchStatus || '').toLowerCase().includes(q)
      || summarizeBatchBanks(row, resolveBankName).toLowerCase().includes(q)
    ));
  }, [rows, search, resolveBankName]);

  const gridRows = useMemo(() => filteredRows.map((batch) => ({
    id: batch.paymentBatchId,
    ...batch,
    bankSummary: summarizeBatchBanks(batch, resolveBankName),
    paymentCount: countBatchPayments(batch),
  })), [filteredRows, resolveBankName]);

  const columns = useMemo(() => [
    {
      field: 'batchNumber',
      headerName: 'Batch Number',
      flex: 1.1,
      minWidth: 170,
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 0.9,
      minWidth: 120,
      valueFormatter: (params) => (
        params.value ? formatDateOnly(params.value, config.DateFormat) : '—'
      ),
    },
    {
      field: 'batchStatus',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 100,
      valueFormatter: (params) => params.value || 'DRAFT',
    },
    {
      field: 'totalAmount',
      headerName: 'Total Amount',
      flex: 1,
      minWidth: 140,
      align: 'left',
      headerAlign: 'left',
      valueFormatter: (params) => formatAmount(params.value, config.AmountFormat),
    },
    {
      field: 'bankSummary',
      headerName: 'Banks',
      flex: 1.1,
      minWidth: 160,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'paymentCount',
      headerName: 'Payments',
      flex: 0.6,
      minWidth: 90,
      align: 'left',
      headerAlign: 'left',
    },
    {
      field: 'view',
      headerName: '',
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="View batch details">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedBatch(params.row);
              setDetailOpen(true);
            }}
            sx={{ color: 'primary.main' }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ], [config.AmountFormat, config.DateFormat]);

  if (loading && rows.length === 0) {
    return (
      <Box sx={{ ...harmonatePageSx, justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress sx={{ color: 'white' }} />
      </Box>
    );
  }

  return (
    <Box sx={harmonatePageSx}>
      <ApHubBreadcrumb
        variant="harmonate"
        segments={[
          { label: 'Payment Proposal', path: '/treasury-payment/payment-proposal' },
          { label: 'Payment Batches' },
        ]}
      />

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" sx={harmonatePageTitleSx}>Payment Batches</Typography>
        <IconButton onClick={() => loadBatches()} aria-label="Refresh payment batches" sx={harmonatePlainIconButtonSx}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ ...harmonateCardSx, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={harmonateSectionHeaderSx}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Batches ({gridRows.length})
          </Typography>
          <TextField
            size="small"
            placeholder="Search batch number, status, or bank"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
          />
        </Box>
        <DataGridPro
          rows={gridRows}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          autoHeight={false}
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          sx={{
            ...harmonateDataGridSx,
            flex: 1,
            minHeight: 0,
            height: '100%',
          }}
        />
      </Paper>

      <PaymentBatchDetailDialog
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          setSelectedBatch(null);
        }}
        batch={selectedBatch}
        businessUnitName={selectedBusinessUnitName}
      />
    </Box>
  );
};

export default PaymentBatches;
